import React, { useState, useEffect, useRef } from 'react';
import { type NavigateOptions, type To, useLocation, useParams } from 'react-router-dom';
import type { Account, Transaction } from '../types';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchAccounts,
  updateAccountBalance as updateAccountBalanceAction,
} from '../store/slices/accountsSlice';
import { fetchTransactionsByAccount } from '../store/slices/transactionsSlice';
import { fetchTags, selectTagMap } from '../store/slices/tagsSlice';
import ScreenContainer from '../components/ScreenContainer';
import HeaderActionsMenu from '../components/HeaderActionsMenu';
import { useNavigation } from '../hooks/useNavigation';
import ParentAccountCard from '../components/ParentAccountCard';
import TransactionList from '../components/TransactionList';
import AddTransactionFlow from '../components/AddTransactionFlow';

interface AccountDetailsRouteState {
  account?: Account;
  isChild?: boolean;
}

interface AccountDetailsScreenProps {
  getTransactionDetailPath?: (transaction: Transaction) => To;
  getTransactionSplitPath?: (transaction: Transaction) => To;
  getTransactionNavigationState?: (transaction: Transaction) => NavigateOptions['state'];
}

const AccountDetailsScreen: React.FC<AccountDetailsScreenProps> = ({
  getTransactionDetailPath,
  getTransactionSplitPath,
  getTransactionNavigationState,
}) => {
  const dispatch = useAppDispatch();
  const { navigateTo } = useNavigation();
  const { accountId } = useParams<{ accountId: string }>();
  const location = useLocation();
  const routeState = location.state as AccountDetailsRouteState | null;
  const fallbackAccount = routeState?.account;

  const parsedAccountId = Number(accountId);
  const resolvedAccountId = Number.isFinite(parsedAccountId)
    ? parsedAccountId
    : fallbackAccount?.id;

  const isChild = routeState?.isChild ?? (fallbackAccount?.parentAccountId != null);

  const [showBalanceModal, setShowBalanceModal] = useState<boolean>(false);
  const [isBalanceModalRendered, setIsBalanceModalRendered] = useState<boolean>(false);
  const [isBalanceModalVisible, setIsBalanceModalVisible] = useState<boolean>(false);
  const [newBalance, setNewBalance] = useState<string>('');
  const [balanceError, setBalanceError] = useState<string | null>(null);
  const [isUpdatingBalance, setIsUpdatingBalance] = useState<boolean>(false);
  const requestedAccountTransactionsRef = useRef<Set<number>>(new Set());

  const { status: accountsStatus } = useAppSelector((state) => state.accounts);
  const { status: tagsStatus } = useAppSelector((state) => state.tags);
  const allAccounts = useAppSelector((state) => state.accounts.accounts);
  const tagMap = useAppSelector(selectTagMap);

  useEffect(() => {
    if (showBalanceModal) {
      setIsBalanceModalRendered(true);
      const showTimer = window.setTimeout(() => setIsBalanceModalVisible(true), 10);
      return () => window.clearTimeout(showTimer);
    }

    setIsBalanceModalVisible(false);
    const hideTimer = window.setTimeout(() => {
      setIsBalanceModalRendered(false);
      setNewBalance('');
      setBalanceError(null);
    }, 200);

    return () => window.clearTimeout(hideTimer);
  }, [showBalanceModal]);

  // Keep account data fresh when route is loaded directly.
  useEffect(() => {
    if (accountsStatus === 'idle') {
      dispatch(fetchAccounts());
    }
  }, [accountsStatus, dispatch]);

  useEffect(() => {
    if (tagsStatus === 'idle') {
      dispatch(fetchTags());
    }
  }, [dispatch, tagsStatus]);

  const updatedAccount = useAppSelector((state) => {
    if (!resolvedAccountId) {
      return fallbackAccount ?? null;
    }

    const storeAccount = state.accounts.accounts.find((acc) => acc.id === resolvedAccountId);
    if (!storeAccount) {
      if (!fallbackAccount) {
        return null;
      }

      const fallbackParent = fallbackAccount.parentAccountId
        ? state.accounts.accounts.find((acc) => acc.id === fallbackAccount.parentAccountId)
        : null;

      return {
        ...fallbackAccount,
        creditCardBill: fallbackParent?.creditCardBill ?? fallbackAccount.creditCardBill,
      };
    }

    const parentAccount = storeAccount.parentAccountId
      ? state.accounts.accounts.find((acc) => acc.id === storeAccount.parentAccountId)
      : null;

    const children = state.accounts.accounts.filter(
      (acc) => acc.parentAccountId === resolvedAccountId
    );

    return {
      ...storeAccount,
      creditCardBill: parentAccount?.creditCardBill ?? storeAccount.creditCardBill,
      children:
        children.length > 0
          ? children
          : (storeAccount.children ?? fallbackAccount?.children),
    };
  });

  // Transactions state
  const { transactions, status: transactionsStatus, error: transactionsError } = useAppSelector((state) => state.transactions);

  // Filter transactions for this specific account.
  const accountTransactions = transactions.filter((tx) => {
    if (!updatedAccount) {
      return false;
    }

    if (tx.account?.id === updatedAccount.id) {
      return true;
    }

    if (tx.account?.parentAccountId && tx.account.parentAccountId === updatedAccount.id) {
      return true;
    }

    return false;
  });

  // Fetch transactions for this account when component mounts.
  useEffect(() => {
    if (!updatedAccount) {
      return;
    }

    if (accountTransactions.length > 0) {
      requestedAccountTransactionsRef.current.add(updatedAccount.id);
      return;
    }

    if (requestedAccountTransactionsRef.current.has(updatedAccount.id)) {
      return;
    }

    if (transactionsStatus === 'loading' || transactionsStatus === 'loadingMore') {
      return;
    }

    requestedAccountTransactionsRef.current.add(updatedAccount.id);
    dispatch(fetchTransactionsByAccount({ accountId: updatedAccount.id }))
      .unwrap()
      .catch(() => {
        requestedAccountTransactionsRef.current.delete(updatedAccount.id);
      });
  }, [dispatch, updatedAccount, accountTransactions.length, transactionsStatus]);

  if (!updatedAccount) {
    return (
      <ScreenContainer title="Account Details">
        <div className="p-6 text-center text-muted-foreground">
          {accountsStatus === 'loading' ? 'Loading account...' : 'Account not found.'}
        </div>
      </ScreenContainer>
    );
  }

  const handleEditBalance = () => {
    setShowBalanceModal(true);
    setNewBalance(updatedAccount.balance.toString());
    setBalanceError(null);
  };

  const handleCancelEdit = () => {
    setShowBalanceModal(false);
  };

  const handleSaveBalance = async () => {
    setBalanceError(null);

    // Validate the input
    const balanceValue = parseFloat(newBalance);
    if (isNaN(balanceValue)) {
      setBalanceError('Please enter a valid number');
      return;
    }

    setIsUpdatingBalance(true);
    try {
      await dispatch(updateAccountBalanceAction({
        accountId: updatedAccount.id,
        newBalance: balanceValue
      })).unwrap();
      setShowBalanceModal(false);
      console.log(`Account ${updatedAccount.id} balance updated successfully to ${balanceValue}`);
    } catch (error: any) {
      console.error('Failed to update account balance:', error);
      const message = error?.message || 'Failed to update account balance. Please try again.';
      setBalanceError(message);
    } finally {
      setIsUpdatingBalance(false);
    }
  };

  const isInitialTransactionsLoading = transactionsStatus === 'loading' && accountTransactions.length === 0;
  const shouldShowTransactionsError = !!transactionsError && accountTransactions.length === 0;
  const shouldShowNoTransactions =
    !isInitialTransactionsLoading &&
    !shouldShowTransactionsError &&
    accountTransactions.length === 0;

  const listTopContent = (
    <>
      <div className="w-full pt-3">
        <div
          className="relative w-full overflow-x-auto overflow-y-hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          <div className="py-2 w-max min-w-full flex justify-center px-4">
            <ParentAccountCard
              parentAccount={updatedAccount}
              onEditBalance={handleEditBalance}
              showEditBalance={true}
              showBalance={!isChild}
            />
          </div>
          <span className="absolute hidden sm:block flex-shrink-0 left-0 h-full w-6 bg-gradient-to-r from-background to-transparent" />
          <span className="absolute hidden sm:block flex-shrink-0 right-0 h-full w-6 bg-gradient-to-l from-background to-transparent" />
        </div>
      </div>

      {isInitialTransactionsLoading && (
        <div className="w-full px-4 pt-3 text-center text-muted-foreground">
          Loading transactions...
        </div>
      )}

      {shouldShowTransactionsError && (
        <div className="w-full px-4 pt-3 text-center text-error">
          Error loading transactions: {transactionsError}
        </div>
      )}

      {shouldShowNoTransactions && (
        <div className="w-full px-4 pt-3 text-center text-muted-foreground">
          No transactions found for this account.
        </div>
      )}
    </>
  );

  return (
    <ScreenContainer
      title="Account Details"
      headerAction={(
        <AddTransactionFlow
          accounts={allAccounts}
          tagMap={tagMap}
          preselectedAccount={updatedAccount}
          renderTrigger={(openAddTransactionFlow) => (
            <HeaderActionsMenu
              buttonAriaLabel="Open account actions"
              items={[
                ...(updatedAccount.type === 'CREDIT_CARD'
                  ? [{
                      label: 'Bill History',
                      onSelect: () => {
                        const billHistoryAccount = updatedAccount.parentAccountId
                          ? allAccounts.find((account) => account.id === updatedAccount.parentAccountId) ?? updatedAccount
                          : updatedAccount;
                        navigateTo(`/accounts/${billHistoryAccount.id}/credit-card-bills`, {
                          state: { account: billHistoryAccount },
                        });
                      },
                    }]
                  : []),
                {
                  label: 'Add transaction',
                  onSelect: openAddTransactionFlow,
                },
              ]}
            />
          )}
        />
      )}
    >
      <div className="flex h-full w-full flex-col min-h-0">
        <div className="w-full flex-1 min-h-0">
          <TransactionList
            transactions={accountTransactions}
            className="h-full"
            topContent={listTopContent}
            getTransactionDetailPath={getTransactionDetailPath}
            getTransactionSplitPath={getTransactionSplitPath}
            getTransactionNavigationState={getTransactionNavigationState}
          />
        </div>
      </div>

      {/* Balance Update Modal */}
      {isBalanceModalRendered && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-[opacity,background-color,backdrop-filter] duration-200 ease-out ${isBalanceModalVisible ? 'bg-black/50 opacity-100 backdrop-blur-sm' : 'bg-black/0 opacity-0 backdrop-blur-none'}`}
          onClick={handleCancelEdit}
        >
          <div
            className={`w-full max-w-sm rounded-xl border border-border bg-card p-5 text-card-foreground shadow-2xl transition-[opacity,transform] duration-200 ease-out ${isBalanceModalVisible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 scale-95 opacity-0'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="balance-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="balance-modal-title" className="text-lg font-semibold text-foreground mb-4">Update Account Balance</h3>


            <div className="mb-4">
              <label className="block text-sm text-muted-foreground mb-2">New Balance</label>
              <input
                type="number"
                step="0.01"
                value={newBalance}
                onChange={(e) => setNewBalance(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter new balance"
                disabled={isUpdatingBalance}
                autoFocus
              />
              {balanceError && (
                <p className="text-sm text-error mt-1">{balanceError}</p>
              )}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={isUpdatingBalance}
                className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveBalance}
                disabled={isUpdatingBalance || !newBalance}
                className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center"
              >
                {isUpdatingBalance ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full mr-2"></div>
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </ScreenContainer>
  );
};

export default AccountDetailsScreen;
